'use client';

import { buttonVariants } from '@/components/ui/button';
import { Dock, DockIcon } from '@/components/ui/dock';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { openWindow } from '@/redux/slice/windowSlice';
import { RootState } from '@/redux/store';
import { WindowData } from '@/utils/types/window';
import { ChevronUp } from 'lucide-react';
import Image from 'next/image';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSwipeable } from 'react-swipeable';
import useMainMenuPermissions from '../permissions/useMainMenuFormPermissions';
const MainMenu = () => {
  const dispatch = useDispatch();
  const windows = useSelector((state: RootState) => state.windows.windows);
  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const [popupWindows, setPopupWindows] = useState<WindowData[] | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [isDockVisible, setIsDockVisible] = useState(false);
  const dockRef = useRef<HTMLDivElement>(null);
  const {
    menuItems,
    isLoading
  } = useMainMenuPermissions();
  const handlers = useSwipeable({
    onSwipedLeft: () => setIsDockVisible(false),
    onSwipedRight: () => setIsDockVisible(true)
  });
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  useEffect(() => {
    if (popupVisible && activePopup !== null) {
      let menuWindows;
      if (activePopup === 'all') {
        menuWindows = windows.filter((w: WindowData) => !w.isWindowOpen);
      } else {
        menuWindows = windows.filter((w: WindowData) => w.title === activePopup);
      }
      if (menuWindows.length > 0) {
        setPopupWindows(menuWindows);
      }
    }
  }, [windows]);
  useEffect(() => {
    const handleDockClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if the click is on the dock, popup, or any of their children
      const isClickInsideDock = dockRef.current?.contains(target);
      const isClickInsidePopup = popupRef.current?.contains(target);
      if (!isClickInsideDock && !isClickInsidePopup) {
        setIsDockVisible(false);
        setPopupVisible(false); // Also close popup
      }
    };
    if (isDockVisible) {
      // Use a small delay to prevent immediate closing on open
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleDockClickOutside);
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleDockClickOutside);
      };
    }
  }, [isDockVisible]);
  const handleMenuClick = (event: React.MouseEvent, title: string, componentKey: string) => {
    event.currentTarget.getBoundingClientRect();
    dispatch(openWindow({
      title,
      componentKey,
      id: null
    }));
    setIsDockVisible(false);
  };
  const handleMouseContextMenu = (name: string, event: React.MouseEvent) => {
    event.preventDefault();
    const menuWindows = windows.filter((w: WindowData) => w.title === name);
    if (menuWindows.length > 0) {
      setActivePopup(name);
      setPopupWindows(menuWindows);
      setPopupVisible(true);
    }
  };

  // Close dock on outside click
  useEffect(() => {
    // const menuWindows = windows.filter((w: WindowData) => !w.isWindowOpen);
    setPopupWindows(windows);
  }, [windows]); // Add isDockVisible as dependency

  const handleMainMenuClick = () => {
    setPopupVisible(true);
    setActivePopup('all');
  };
  const openPopupWindow = (title: string, componentKey: string, windowId: number) => {
    dispatch(openWindow({
      title,
      componentKey,
      id: windowId
    }));
    setPopupVisible(false);
    setIsDockVisible(false);
  };
  const handleClickOutside = (event: MouseEvent) => {
    if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
      setPopupVisible(false);
    }
  };
  const filteredMenuItems = Object.entries(menuItems);
  return <div className="absolute bottom-2 left-0 top-[150px] z-[40] m-0 m-[unset] w-[max-content] select-none md:right-4 lg:m-auto" data-sentry-component="MainMenu" data-sentry-source-file="MainMenu.tsx">
      <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="MainMenu.tsx">
        <div {...handlers} className="relative">
          {!isDockVisible && <>
              <button className="absolute left-0 top-1/2 z-50 flex h-32 w-5 -translate-y-1/2 transform items-center justify-center rounded-r-xl bg-gray-500 opacity-75 focus:outline-none lg:hidden" onClick={() => setIsDockVisible(true)} aria-label="Show dock">
                <Image src="/images/next.png" alt="Show dock" width={18} height={18} />
              </button>
              <button className="fixed bottom-0 left-1/2 hidden w-32 -translate-x-1/2 items-center justify-center rounded-t-[10px] bg-gray-500 opacity-75 focus:outline-none lg:flex" onClick={() => setIsDockVisible(true)} aria-label="Show dock">
                <ChevronUp className="h-6 w-6 text-white" />
              </button>
            </>}

          <div className={cn('fixed left-0 top-[54px] h-full w-auto flex-col p-0 md:top-[90px] lg:bottom-0 lg:top-auto lg:m-auto lg:flex lg:h-auto lg:w-full lg:gap-0 lg:py-1', isDockVisible ? 'translate-x-0 lg:translate-y-[0px]' : '-translate-x-full lg:translate-x-0 lg:translate-y-[60px]')}>
            <Dock ref={dockRef} direction="middle" className="m-0 flex h-auto flex-col gap-0 p-0 lg:m-auto lg:flex-row lg:py-1" data-sentry-element="Dock" data-sentry-source-file="MainMenu.tsx">
              {!isLoading && filteredMenuItems.map(([name, mainmenu]) => {
              const IconComponent = mainmenu.icon;
              const window = windows.find((w: WindowData) => w.componentKey === mainmenu.module);
              const isWindowExist = !!window;
              return <DockIcon key={name}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button aria-label={mainmenu.name} className={cn(buttonVariants({
                      variant: isWindowExist ? 'active' : 'bottomnav',
                      size: 'icon'
                    }), 'size-[24px] rounded-[6px] px-[3px] py-[3px] md:size-[32px] md:px-[5px] md:py-[5px] lg:size-11 lg:rounded-[8px] lg:px-2 lg:py-2')} onClick={e => handleMenuClick(e, isWindowExist ? window?.title : mainmenu.name, mainmenu.module)} onContextMenu={e => {
                      handleMouseContextMenu(mainmenu.name, e);
                    }}>
                            {IconComponent && <IconComponent />}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{mainmenu.name}</p>
                        </TooltipContent>
                      </Tooltip>
                      {activePopup === mainmenu.name && popupVisible && popupWindows?.length ? <div ref={popupRef} className="supports-backdrop-blur:bg-white/10 absolute bottom-[66px] left-1/2 z-50 mb-[unset] mt-[unset] flex max-h-[200px] -translate-x-1/2 transform flex-col gap-y-2 overflow-x-auto rounded-sm border-[1px] border-[#ddd] bg-[rgba(255,255,255,0.9)] px-4 py-3 shadow-lg backdrop-blur-md">
                          {popupWindows?.map(visibleWindow => <button onClick={() => openPopupWindow(visibleWindow.title, visibleWindow.componentKey, visibleWindow.id)} className="flex min-w-[125px] flex-col items-center justify-center gap-y-1 rounded-[8px] bg-[#d5d5d578] px-3 py-2 text-xs font-semibold hover:bg-[#fff]" key={visibleWindow.id}>
                              <span className="text-xs text-[#2e2e2e]">{visibleWindow.title}</span>
                            </button>)}
                        </div> : null}
                    </DockIcon>;
            })}
              <DockIcon key="all" data-sentry-element="DockIcon" data-sentry-source-file="MainMenu.tsx">
                <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="MainMenu.tsx">
                  <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="MainMenu.tsx">
                    <button aria-label="all-windows" className={cn(buttonVariants({
                    variant: 'bottomnav',
                    size: 'icon'
                  }), 'size-11 rounded-[8px] px-3 py-3')} onClick={handleMainMenuClick}>
                      <Image priority src="/images/waffle-icon.svg" alt="Home Icon" width={50} height={50} data-sentry-element="Image" data-sentry-source-file="MainMenu.tsx" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="MainMenu.tsx">
                    <p>Show Open Windows</p>
                  </TooltipContent>
                </Tooltip>
                {activePopup === 'all' && popupVisible && (popupWindows?.length ? <div ref={popupRef} className="supports-backdrop-blur:bg-white/10 absolute bottom-[10px] left-1/2 z-50 ml-28 flex max-h-[300px] -translate-x-1/2 transform flex-col gap-y-2 overflow-x-auto rounded-sm border-[1px] border-[#ddd] bg-[rgba(255,255,255,0.9)] px-4 py-3 shadow-lg backdrop-blur-md md:bottom-[66px] lg:ml-0">
                      {popupWindows.map(visibleWindow => <button onClick={() => openPopupWindow(visibleWindow.title, visibleWindow.componentKey, visibleWindow.id)} className="flex min-w-[125px] flex-col items-center justify-center gap-y-1 rounded-[8px] bg-[#d5d5d578] px-3 py-2 text-xs font-semibold hover:bg-[#fff]" key={visibleWindow.id}>
                          <span className="text-xs text-[#2e2e2e]">{visibleWindow.title}</span>
                        </button>)}
                    </div> : <div ref={popupRef} className="supports-backdrop-blur:bg-white/10 absolute bottom-[10px] left-1/2 z-50 ml-28 flex max-h-[300px] -translate-x-1/2 transform flex-col gap-y-2 overflow-x-auto rounded-sm border-[1px] border-[#ddd] bg-[rgba(255,255,255,0.9)] px-4 py-3 shadow-lg backdrop-blur-md md:bottom-[66px] lg:ml-0">
                      <p className="min-w-[125px] text-sm text-[#2e2e2e]">No hidden found</p>
                    </div>)}
              </DockIcon>
            </Dock>
          </div>
        </div>
      </TooltipProvider>
    </div>;
};
export default memo(MainMenu);